import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Clock, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface CronJobExecution {
  id: string;
  job_type: string;
  analysis_date?: string;
  created_at: string;
  processing_time_ms: number | null;
  families_processed?: number;
  notifications_processed?: number;
  items_processed?: number;
  users_processed?: number;
  status: 'success' | 'failed' | 'running' | 'completed';
}

interface JobGroup {
  type: string;
  executions: CronJobExecution[];
}

export const CronJobHistory: React.FC = () => {
  const [jobGroups, setJobGroups] = useState<JobGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchCronHistory = async () => {
    setIsLoading(true);
    try {
      // Fetch from cron_job_executions table - filter for the job types from actual cron functions
      const { data: cronData, error: cronError } = await supabase
        .from('cron_job_executions')
        .select('*')
        .in('job_type', ['Meal Base Data Analysis', 'Meal Suggestions Generation', 'Weekly Wellness Generation'])
        .order('created_at', { ascending: false })
        .limit(45); // Get more to ensure we have enough for each type

      if (cronError) {
        console.error('Error fetching cron history:', cronError);
        toast.error('Failed to fetch cron job history');
        return;
      }

      const executions: CronJobExecution[] = [];

      // Transform cron job data
      if (cronData) {
        cronData.forEach(item => {
          executions.push({
            id: item.id,
            job_type: item.job_type,
            created_at: item.created_at,
            processing_time_ms: item.processing_time_ms,
            families_processed: item.families_processed || 0,
            status: item.status === 'success' || item.status === 'completed' ? 'success' : 
                   item.status === 'running' ? 'running' : 'failed'
          });
        });
      }

      // Group by job type and take last 3 for each
      const grouped = executions.reduce((acc, execution) => {
        const existing = acc.find(group => group.type === execution.job_type);
        if (existing) {
          existing.executions.push(execution);
        } else {
          acc.push({
            type: execution.job_type,
            executions: [execution]
          });
        }
        return acc;
      }, [] as JobGroup[]);

      // Limit to last 3 for each job type
      const limitedGroups = grouped.map(group => ({
        ...group,
        executions: group.executions.slice(0, 3)
      }));

      setJobGroups(limitedGroups);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Failed to fetch cron history:', err);
      toast.error('Failed to fetch cron job history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCronHistory();
  }, []);

  const getStatusBadge = (execution: CronJobExecution) => {
    if (execution.status === 'success') {
      return <Badge variant="default" className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Success</Badge>;
    } else if (execution.status === 'running') {
      return <Badge variant="default" className="bg-blue-600"><Clock className="h-3 w-3 mr-1" />Running</Badge>;
    } else {
      return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    
    // Less than 1 second - show milliseconds
    if (ms < 1000) return `${ms}ms`;
    
    // Less than 1 minute - show seconds with 1 decimal
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    
    // Less than 1 hour - show minutes with 2 decimals
    if (ms < 3600000) return `${(ms / 60000).toFixed(2)}min`;
    
    // 1 hour or more - show hours with 2 decimals
    return `${(ms / 3600000).toFixed(2)}hr`;
  };

  const getItemsProcessed = (execution: CronJobExecution) => {
    if (execution.families_processed) {
      if (execution.job_type === 'AI Dinner Suggestions' && execution.items_processed) {
        return `${execution.families_processed} families`;
      }
      return `${execution.families_processed} families`;
    }
    if (execution.notifications_processed) return `${execution.notifications_processed} notifications`;
    if (execution.items_processed) return `${execution.items_processed} items`;
    return '0';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            <CardTitle>Cron Job Execution History</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Last updated: {formatDistanceToNow(lastRefresh, { addSuffix: true })}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchCronHistory}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            <span>Loading cron job history...</span>
          </div>
        ) : jobGroups.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No cron job executions found
          </div>
        ) : (
          <div className="space-y-6">
            {jobGroups.map((group) => (
              <div key={group.type} className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground">{group.type}</h3>
                
                <div className="grid grid-cols-3 md:grid-cols-4 gap-4 pb-2 border-b text-sm font-medium text-muted-foreground">
                  <div>Date</div>
                  <div>Status</div>
                  <div className="hidden md:block">Duration</div>
                  <div className="hidden md:block">Executed</div>
                </div>
                
                {group.executions.map((execution) => (
                  <div key={execution.id} className="grid grid-cols-3 md:grid-cols-4 gap-4 py-2 text-sm border-b border-muted/30 hover:bg-muted/20 rounded">
                     <div className="font-mono text-xs">
                       {new Date(execution.created_at).toISOString().split('T')[0]}
                       {execution.job_type === 'Weekly Wellness Analysis' && execution.analysis_date && (
                         <div className="text-xs text-muted-foreground mt-1 font-medium">
                           {execution.analysis_date}
                         </div>
                       )}
                     </div>
                    <div>
                      {getStatusBadge(execution)}
                    </div>
                    <div className="hidden md:block">
                      <Badge variant="outline">
                        {formatDuration(execution.processing_time_ms)}
                      </Badge>
                    </div>
                    <div className="hidden md:block text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(execution.created_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};